<script setup lang="ts">
/**
 * AURA Chat Layout - Main chat interface layout
 * [FE] Implementar layout principal do chat
 */
import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTypewriter } from '@/composables/useTypewriter';
import { UserIcon, PaperAirplaneIcon, PlusIcon } from '@heroicons/vue/solid';
import type { AuraAction, AuraMessage } from '@/types';
import { formatAuraDateLabel, formatAuraMetric } from '@/utils/auraPresentation';
import AuraActionCard from './components/AuraActionCard.vue';
import AuraChartBlock from './components/AuraChartBlock.vue';
import AuraInsightCard from './components/AuraInsightCard.vue';
import AuraRichText from './components/AuraRichText.vue';
import AuraVisualizationPanel from './components/AuraVisualizationPanel.vue';

// AURA Icons
import auraMainIcon from '@/assets/icons/aura-main.svg';
import auraConversationIcon from '@/assets/icons/aura-conversation.svg';

interface Props {
    messages?: AuraMessage[];
    isLoading?: boolean;
    disabled?: boolean;
    emptyPrompts?: string[];
    contextTitle?: string;
    loadingMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
    messages: () => [],
    isLoading: false,
    disabled: false,
    emptyPrompts: () => [],
    contextTitle: '',
    loadingMessage: '',
});
const { t, locale } = useI18n();

const emit = defineEmits<{
    (e: 'send', message: string): void;
    (
        e: 'retry-failed-question',
        payload: { userMsgIndex: number; auraMsgIndex: number; question: string },
    ): void;
    (
        e: 'send-with-filter',
        payload: { message: string; filterKey: string; filterId: number },
    ): void;
}>();

const inputMessage = ref('');
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const MAX_MESSAGE_LENGTH = 2000;

const canSend = computed(() => {
    return inputMessage.value.trim().length > 0 && !props.isLoading && !props.disabled;
});

const messageLength = computed(() => inputMessage.value.length);
const remainingCharacters = computed(() => Math.max(MAX_MESSAGE_LENGTH - messageLength.value, 0));

const hasMessages = computed(() => props.messages.length > 0);

const adjustTextareaHeight = () => {
    if (textareaRef.value) {
        textareaRef.value.style.height = 'auto';
        textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 120)}px`;
    }
};

const retryFailedQuestion = (auraMsgIndex: number) => {
    const userMsgIndex = auraMsgIndex - 1;
    let questionText = '';
    if (userMsgIndex >= 0 && props.messages[userMsgIndex]?.type === 'user') {
        questionText = props.messages[userMsgIndex].content;
    }

    inputMessage.value = questionText;

    nextTick(() => {
        textareaRef.value?.focus();
        adjustTextareaHeight();
    });

    emit('retry-failed-question', {
        userMsgIndex,
        auraMsgIndex,
        question: questionText,
    });
};

const sendMessage = () => {
    if (!canSend.value) return;

    const message = inputMessage.value.trim();
    emit('send', message);
    inputMessage.value = '';

    // Reset textarea height
    if (textareaRef.value) {
        textareaRef.value.style.height = 'auto';
    }

    // Auto-scroll to bottom
    nextTick(() => {
        scrollToBottom();
    });
};

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
    }
};

const handleInput = (e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
};

const scrollToBottom = () => {
    if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
};

// Auto-scroll when messages change
watch(
    () => props.messages.length,
    () => {
        nextTick(() => {
            scrollToBottom();
        });
    },
);

const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const selectPrompt = (prompt: string) => {
    inputMessage.value = prompt;
    if (textareaRef.value) {
        textareaRef.value.focus();
    }
};

const focusComposer = () => {
    textareaRef.value?.focus();
};

const executeAction = (action: AuraAction) => {
    const followUpQuestion = action.followUpQuestion?.trim();
    if (!followUpQuestion || props.isLoading || props.disabled) return;
    emit('send', followUpQuestion);
};

const getFilterDisambiguationOptions = (message: AuraMessage) => {
    return (
        message.response?.status?.details?.options ||
        message.response?.status?.filter_conflict?.matches ||
        []
    );
};

const getFilterDisambiguationFilterKey = (message: AuraMessage) => {
    return message.response?.status?.filter_conflict?.filterKey || 'userId';
};

const getFilterDisambiguationLabel = (message: AuraMessage) => {
    const code = message.response?.status?.code;
    if (code === 'AGENT_NAME_AMBIGUOUS') {
        return t('aura.chat.selectAgent');
    }
    return t('aura.chat.selectFilterOption');
};

const getKpiUnit = (message: AuraMessage, unit?: string): string | undefined => {
    return (
        unit || message.response?.chart?.unit || message.response?.visualization?.charts[0]?.unit
    );
};

// Typewriter para loading message — anima quando isLoading=true e loadingMessage muda
const { displayed: typewriterText } = useTypewriter(() =>
    props.isLoading ? (props.loadingMessage ?? '') : '',
);

defineExpose({
    focusComposer,
});
</script>

<template>
    <div class="aura-chat-layout flex h-full min-h-0 flex-col bg-white">
        <!-- Messages area with internal scroll -->
        <div
            ref="messagesContainer"
            class="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6"
        >
            <!-- Empty state with example prompts -->
            <div
                v-if="!hasMessages && !isLoading"
                class="flex h-full flex-col items-center justify-center px-2 sm:px-4"
            >
                <!-- Icon -->
                <img :src="auraMainIcon" class="mb-6 h-16 w-16" alt="AURA" />

                <!-- Title -->
                <h2 class="mb-2 text-center text-xl font-bold text-[#242B59] sm:text-2xl">
                    {{ $t('aura.chat.emptyState.title') }}
                </h2>
                <p class="mb-8 text-center text-[#626468]">
                    {{
                        props.contextTitle
                            ? $t('aura.chat.emptyState.contextSubtitle', {
                                  context: props.contextTitle,
                              })
                            : $t('aura.chat.emptyState.fallbackSubtitle')
                    }}
                </p>

                <!-- Example prompts grid -->
                <div class="grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                    <button
                        v-for="(prompt, index) in props.emptyPrompts"
                        :key="index"
                        class="flex items-center justify-between gap-3 rounded-2xl border border-[#E5E5E5] bg-white p-4 text-left shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] transition-all hover:border-[#48DDC3] hover:shadow-md"
                        @click="selectPrompt(prompt)"
                    >
                        <span class="text-sm font-medium text-[#626468]">{{ prompt }}</span>
                        <PlusIcon class="h-5 w-5 flex-shrink-0 text-[#A1A1A1]" />
                    </button>
                </div>
            </div>

            <!-- Messages list -->
            <template v-else>
                <div
                    v-for="(message, index) in props.messages"
                    :key="message.id || index"
                    :class="[
                        'flex min-w-0 gap-3',
                        message.type === 'user' ? 'flex-row-reverse' : 'flex-row',
                    ]"
                >
                    <!-- Avatar -->
                    <div
                        :class="[
                            'flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl',
                            message.type === 'user' ? 'bg-[#242B59] text-white' : 'bg-[#48DDC3]/20',
                        ]"
                    >
                        <UserIcon v-if="message.type === 'user'" class="h-5 w-5" />
                        <img v-else :src="auraConversationIcon" class="h-full w-full" alt="AURA" />
                    </div>

                    <!-- Message bubble -->
                    <div
                        class="aura-message-bubble"
                        :class="[
                            'min-w-0',
                            message.type === 'user'
                                ? 'max-w-[90%] rounded-2xl rounded-br-md bg-[#242B59] px-4 py-2.5 text-white sm:max-w-[85%]'
                                : message.response
                                  ? 'max-w-full flex-1'
                                  : message.type === 'error'
                                    ? 'max-w-[90%] rounded-2xl rounded-bl-md border border-red-200 bg-red-50 px-4 py-2.5 text-red-800 sm:max-w-[85%]'
                                    : 'max-w-[90%] rounded-2xl rounded-bl-md bg-gray-50 px-4 py-2.5 text-[#242B59] sm:max-w-[85%]',
                        ]"
                    >
                        <!-- Simple text message -->
                        <AuraRichText
                            v-if="!message.response"
                            class="whitespace-pre-wrap text-sm"
                            :text="message.content"
                        />

                        <!-- Structured response -->
                        <div v-else class="w-full space-y-4">
                            <!-- Summary / blocked message -->
                            <div
                                class="max-w-[90%] rounded-2xl rounded-bl-md bg-gray-50 px-4 py-2.5 text-[#242B59] sm:max-w-[85%]"
                            >
                                <AuraRichText
                                    class="whitespace-pre-wrap text-sm"
                                    :text="
                                        message.response.summary?.content ||
                                        message.response.status?.message ||
                                        message.content
                                    "
                                />
                            </div>

                            <!-- Retry button for temporal conflicts -->
                            <div
                                v-if="
                                    message.response?.status?.code === 'BLOCKED_TEMPORAL_CONFLICT'
                                "
                                class="mt-2 pt-1"
                            >
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-[#A16207] shadow-sm transition-all hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1"
                                    @click="retryFailedQuestion(index)"
                                >
                                    <svg
                                        class="h-3.5 w-3.5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        stroke-width="2"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3 3L22 4"
                                        />
                                    </svg>
                                    Perguntar novamente
                                </button>
                            </div>

                            <!-- Disambiguation options (agent / queue) -->
                            <div
                                v-if="
                                    [
                                        'FILTER_DISAMBIGUATION',
                                        'DISAMBIGUATION_REQUIRED',
                                        'AGENT_NAME_AMBIGUOUS',
                                    ].includes(message.response?.status?.code)
                                "
                                class="mt-3 space-y-2"
                            >
                                <p
                                    class="text-xs font-semibold uppercase tracking-wider text-[#737373]"
                                >
                                    {{ getFilterDisambiguationLabel(message) }}
                                </p>
                                <div class="flex flex-wrap gap-2">
                                    <button
                                        v-for="match in getFilterDisambiguationOptions(message)"
                                        :key="match.id"
                                        type="button"
                                        class="rounded-lg border border-[#48DDC3] bg-[#48DDC3]/10 px-3 py-1.5 text-sm font-medium text-[#242B59] transition-all hover:bg-[#48DDC3]/30"
                                        @click="
                                            $emit('send-with-filter', {
                                                message: messages[index - 1]?.content,
                                                filterKey:
                                                    getFilterDisambiguationFilterKey(message),
                                                filterId: Number(match.id),
                                            })
                                        "
                                    >
                                        {{ match.label || match.name }}
                                    </button>
                                </div>
                            </div>

                            <!-- KPIs -->
                            <div
                                v-if="message.response.kpis?.items?.length"
                                class="grid grid-cols-2 gap-3"
                            >
                                <div
                                    v-for="kpi in message.response.kpis.items"
                                    :key="kpi.name"
                                    class="rounded-xl border border-[#E5E5E5] bg-white p-3"
                                >
                                    <p class="text-xs text-[#808288]">
                                        {{ formatAuraDateLabel(kpi.name) }}
                                    </p>
                                    <p class="text-lg font-bold text-[#242B59]">
                                        {{
                                            formatAuraMetric(
                                                kpi.value,
                                                getKpiUnit(message, kpi.unit),
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="kpi.trendValue"
                                        :class="[
                                            'text-xs',
                                            kpi.trend === 'up'
                                                ? 'text-green-500'
                                                : kpi.trend === 'down'
                                                  ? 'text-red-500'
                                                  : 'text-gray-400',
                                        ]"
                                    >
                                        {{ kpi.trendValue }}
                                    </p>
                                </div>
                            </div>

                            <!-- Chart -->
                            <AuraVisualizationPanel
                                v-if="message.response.visualization?.status === 'complete'"
                                :plan="message.response.visualization"
                            />
                            <AuraChartBlock
                                v-else-if="message.response.chart?.status === 'complete'"
                                :chart="message.response.chart"
                            />

                            <!-- Insights -->
                            <div
                                v-if="message.response.insights?.items?.length"
                                class="flex flex-wrap gap-3"
                            >
                                <div
                                    v-for="(insight, idx) in message.response.insights.items"
                                    :key="idx"
                                    class="w-full sm:w-auto"
                                >
                                    <AuraInsightCard :insight="insight" />
                                </div>
                            </div>

                            <!-- Actions -->
                            <div v-if="message.response.actions?.items?.length" class="space-y-3">
                                <AuraActionCard
                                    v-for="(action, idx) in message.response.actions.items"
                                    :key="idx"
                                    :action="action"
                                    :loading="isLoading"
                                    :disabled="disabled"
                                    @execute="executeAction"
                                />
                            </div>
                        </div>

                        <span
                            :class="[
                                'mt-1 block text-xs',
                                message.type === 'user' ? 'text-white/70' : 'text-gray-400',
                            ]"
                        >
                            {{ formatTime(message.createdAt) }}
                        </span>
                    </div>
                </div>

                <!-- Loading indicator -->
                <div v-if="isLoading" class="flex flex-row gap-3">
                    <div
                        class="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#48DDC3]/20"
                    >
                        <img :src="auraConversationIcon" class="h-full w-full" alt="AURA" />
                    </div>
                    <div class="rounded-2xl rounded-bl-md bg-gray-50 px-4 py-3">
                        <p
                            v-if="isLoading && loadingMessage"
                            class="aura-typewriter mb-2 text-xs font-medium text-[#626468]"
                        >
                            {{ typewriterText }}
                        </p>
                        <div class="flex items-center gap-2">
                            <div
                                class="h-2 w-2 animate-bounce rounded-full bg-[#48DDC3] [animation-delay:-0.3s]"
                            ></div>
                            <div
                                class="h-2 w-2 animate-bounce rounded-full bg-[#48DDC3] [animation-delay:-0.15s]"
                            ></div>
                            <div class="h-2 w-2 animate-bounce rounded-full bg-[#48DDC3]"></div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <!-- Fixed footer with input -->
        <div
            class="aura-chat-footer shrink-0 border-t border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4"
        >
            <div class="aura-chat-input mx-auto flex max-w-3xl items-end gap-3">
                <!-- Input container with border -->
                <div
                    class="aura-chat-input-field relative flex flex-1 items-center gap-3 rounded-full border border-[#E5E5E5] bg-white px-4 py-3 shadow-[0px_2px_12px_rgba(0,0,0,0.03)] transition-all focus-within:border-[#48DDC3] focus-within:shadow-md"
                >
                    <PlusIcon class="h-5 w-5 text-gray-400" />
                    <textarea
                        ref="textareaRef"
                        v-model="inputMessage"
                        :disabled="disabled"
                        :maxlength="MAX_MESSAGE_LENGTH"
                        rows="1"
                        class="w-full resize-none border-0 bg-transparent text-sm text-[#242B59] placeholder-gray-400 focus:outline-none focus:ring-0"
                        placeholder="Pergunte alguma coisa"
                        @keydown="handleKeydown"
                        @input="handleInput"
                    />
                </div>

                <!-- Send button -->
                <button
                    :disabled="!canSend"
                    class="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-gray-400 transition-all hover:bg-[#242B59] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                    @click="sendMessage"
                >
                    <PaperAirplaneIcon class="h-5 w-5" />
                </button>
            </div>

            <!-- Disclaimer -->
            <div
                class="mt-2 flex items-start justify-between gap-3 text-[11px] leading-4 text-gray-400 sm:mt-3 sm:items-center sm:text-xs"
            >
                <p class="min-w-0">{{ $t('aura.chat.disclaimer') }}</p>
                <p class="shrink-0 whitespace-nowrap">
                    {{ $t('aura.chat.remainingCharacters', { count: remainingCharacters }) }}
                </p>
            </div>
        </div>
    </div>
</template>

<style scoped>
.aura-cursor {
    display: inline-block;
    color: #48ddc3;
    font-weight: 700;
    animation: aura-blink 0.8s step-start infinite;
    margin-left: 1px;
    line-height: 1;
}

@keyframes aura-blink {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0;
    }
}

@media (max-width: 767px) {
    .aura-chat-layout > div:first-child {
        padding: 0.75rem;
    }

    .aura-chat-footer {
        border-top-width: 0;
        padding: 0.5rem 0.75rem 0.625rem;
        box-shadow: 0 -8px 24px rgba(36, 43, 89, 0.06);
    }

    .aura-chat-input {
        gap: 0.5rem;
    }

    .aura-chat-input-field {
        gap: 0.5rem;
        padding: 0.625rem 0.875rem;
    }

    .aura-chat-footer > div:last-child {
        margin-top: 0.375rem;
        font-size: 10px;
        line-height: 0.875rem;
    }
}

@media (orientation: landscape) and (max-height: 500px) {
    .aura-chat-layout > div:first-child {
        gap: 0.625rem;
        padding: 0.5rem 0.75rem;
    }

    .aura-message-bubble {
        padding: 0.5rem 0.625rem !important;
    }

    .aura-message-bubble :deep(.text-sm) {
        font-size: 0.75rem;
        line-height: 1rem;
    }

    .aura-chat-footer {
        padding: 0.375rem 0.75rem;
    }

    .aura-chat-input {
        gap: 0.5rem;
    }

    .aura-chat-input-field {
        gap: 0.5rem;
        padding: 0.5rem 0.75rem;
    }

    .aura-chat-input-field textarea {
        font-size: 0.75rem;
        line-height: 1rem;
    }

    .aura-chat-footer :deep(.h-11) {
        height: 2.5rem;
        width: 2.5rem;
    }

    .aura-chat-footer > div:last-child {
        margin-top: 0.25rem;
        font-size: 10px;
        line-height: 0.875rem;
    }
}
</style>
